/**
 * Manifest for customer-specific code (SA-321 §7.1; slots: step-model E5 spec
 * §7.1, D56; home widgets: Home spec 2026-09-23 D2, §3.3). App.tsx renders
 * routes from SURFACES (an admin-section entry inside the admin area,
 * pages/admin/AdminArea.tsx, which App.tsx hands SURFACES, SA-963) and
 * Sidebar.tsx renders nav entries from it;
 * components/custom/CustomSlots.tsx renders the seven slots (record cards,
 * task views, register views, register columns, home widgets, home tiles, app listeners). Nothing else
 * in the core imports from src/custom.
 */
import { lazy, type ComponentType, type LazyExoticComponent } from 'react'
import { Database, FileSpreadsheet, Plane, Route, SlidersHorizontal, TrendingUp, type LucideIcon } from 'lucide-react'
import type { TaskFormProps } from '@/components/process/TaskForm'
import type { HomeTileEntry, HomeWidgetEntry } from '@/components/custom/homeWidgetTypes'
import type { RegisterViewProps } from '@/components/records/RegisterView'
import type { ModuleSorts } from '@/components/records/registerSort'
import type { Order } from '@/types/commerce'
import type { TaskRead } from '@/types/process'
import { PRICING_SORTS } from './compass/pricingSorts'
import { matchesDecide, matchesPropose, matchesRotation, STEP_DECIDE, STEP_PROPOSE, STEP_RATE_REFERENCES, STEP_ROTATION } from './compass/contract'
import { matchesRateReferences } from './compass/rateReferencesModel'
import { matchesStudyPicker, STEP_DISPATCH } from './compass/studies/pickerModel'
import { matchesBuildCost, STEP_BUILD_COST } from './compass/studies/buildCostModel'
import { matchesRouteStudy, STEP_ROUTE_STUDY } from './compass/studies/routeStudyModel'

export interface SurfaceEntry {
  id: string
  /** Absolute route. A child's route is under its parent's route. */
  route: string
  feature: string
  permission: string
  Component: LazyExoticComponent<ComponentType>
  /** Sidebar placement. Top-level entries only; children have none. */
  nav?: {
    section: 'primary' | 'admin'
    label: string
    icon: LucideIcon
    order?: number
    /** A short word the sidebar shows beside the label (SA-1097: 'Experimental'). */
    tag?: string
  }
  /** Id of a top-level entry; the child renders as a tab inside that entry's shell (SA-323 §7.1). */
  parent?: string
  /** This entry's own tab: for a parent, the first tab of its shell; for a child, its tab. */
  tab?: { label: string; order?: number }
}

export const SURFACES: SurfaceEntry[] = [
  {
    id: 'fleet-locator',
    route: '/fleet',
    nav: { section: 'primary', label: 'Fleet', icon: Plane },
    tab: { label: 'Locator' },
    feature: 'fleet',
    permission: 'fleet:read',
    Component: lazy(() => import('@/custom/fleet/FleetLocatorPage')),
  },
  {
    id: 'fleet-schedule',
    route: '/fleet/schedule',
    parent: 'fleet-locator',
    tab: { label: 'Schedule' },
    feature: 'fleet_scheduling',
    permission: 'fleet:read',
    Component: lazy(() => import('@/custom/fleet/schedule/FleetSchedulePage')),
  },
  {
    // SA-1206 (spec §3.6, D5): the aviation module's Reference data, its own nav entry. The parent's
    // own tab is the datasets index with the review periods; each dataset item adds its tab as a child.
    id: 'aviation-reference',
    route: '/reference-data',
    nav: { section: 'primary', label: 'Reference data', icon: Database },
    tab: { label: 'Datasets' },
    feature: 'aviation',
    permission: 'aviation:reference:read',
    Component: lazy(() => import('@/custom/aviation/reference/ReferenceDataPage')),
  },
  {
    // SA-1203 (spec §7.1): the aircraft model's tab, first among the children (the fleet comes before its datasets).
    id: 'aviation-aircraft',
    route: '/reference-data/aircraft',
    parent: 'aviation-reference',
    tab: { label: 'Aircraft', order: 5 },
    feature: 'aviation',
    permission: 'aviation:reference:read',
    Component: lazy(() => import('@/custom/aviation/reference/AircraftPage')),
  },
  {
    // SA-1207 (spec §4.4): the first dataset's tab; the index's Station charges card links here.
    id: 'aviation-station-charges',
    route: '/reference-data/station-charges',
    parent: 'aviation-reference',
    tab: { label: 'Station charges', order: 10 },
    feature: 'aviation',
    permission: 'aviation:reference:read',
    Component: lazy(() => import('@/custom/aviation/reference/StationChargesPage')),
  },
  {
    // SA-1208: flight leg references, a tab of Reference data.
    id: 'aviation-flight-legs',
    route: '/reference-data/flight-legs',
    parent: 'aviation-reference',
    tab: { label: 'Flight legs', order: 20 },
    feature: 'aviation',
    permission: 'aviation:reference:read',
    Component: lazy(() => import('@/custom/aviation/reference/FlightLegsPage')),
  },
  {
    // SA-1230: stations (facts and assessments), a tab of Reference data.
    id: 'aviation-stations',
    route: '/reference-data/stations',
    parent: 'aviation-reference',
    tab: { label: 'Stations', order: 7 },
    feature: 'aviation',
    permission: 'aviation:reference:read',
    Component: lazy(() => import('@/custom/aviation/reference/StationsPage')),
  },
  {
    // SA-1210: exchange rates, a tab of Reference data.
    id: 'aviation-exchange-rates',
    route: '/reference-data/exchange-rates',
    parent: 'aviation-reference',
    tab: { label: 'Exchange rates', order: 30 },
    feature: 'aviation',
    permission: 'aviation:reference:read',
    Component: lazy(() => import('@/custom/aviation/reference/ExchangeRatesPage')),
  },
  {
    // SA-1288: every setting of the module in one tab, as the Analytics settings tab does it. Order 100 keeps it last
    // as dataset tabs arrive; the Datasets cards link here with `?focus=<dataset id>`.
    id: 'aviation-reference-settings',
    route: '/reference-data/settings',
    parent: 'aviation-reference',
    tab: { label: 'Settings', order: 100 },
    feature: 'aviation',
    permission: 'aviation:reference:read',
    Component: lazy(() => import('@/custom/aviation/reference/ReferenceSettingsPage')),
  },
  {
    // SA-854 (route studies spec R9): its own navigation entry, three tabs on the
    // shared parent shell. A study's page is `?study=RS-<n>[&version=<n>]` on this
    // route; phase 2 writes that URL into `study_link`, so it never changes.
    id: 'compass-route-studies',
    route: '/route-studies',
    nav: { section: 'primary', label: 'Route studies', icon: FileSpreadsheet },
    tab: { label: 'Studies' },
    feature: 'compass_studies',
    permission: 'compass:studies:read',
    Component: lazy(() => import('@/custom/compass/studies/StudiesPage')),
  },
  {
    id: 'compass-route-studies-imports',
    route: '/route-studies/imports',
    parent: 'compass-route-studies',
    tab: { label: 'Workbook imports', order: 1 },
    feature: 'compass_studies',
    permission: 'compass:studies:read',
    Component: lazy(() => import('@/custom/compass/studies/ImportsPage')),
  },
  {
    id: 'compass-route-studies-fuel',
    route: '/route-studies/fuel-prices',
    parent: 'compass-route-studies',
    tab: { label: 'Fuel prices', order: 2 },
    feature: 'compass_studies',
    permission: 'compass:studies:read',
    Component: lazy(() => import('@/custom/compass/studies/FuelPricesPage')),
  },
  {
    // SA-836 (rotations spec §8, ruling 8): its own nav entry. The route
    // exists only with the flag on; the entry and the page need the read.
    id: 'compass-rotations',
    route: '/rotations',
    nav: { section: 'primary', label: 'Rotations', icon: Route },
    tab: { label: 'Rotations' },
    feature: 'compass_rotations',
    permission: 'compass:rotations:read',
    Component: lazy(() => import('@/custom/compass/rotations/RotationsPage')),
  },
  {
    // SA-841 (CT4): contracts are a tab beside Rotations, the route studies shape.
    id: 'compass-rotations-contracts',
    route: '/rotations/contracts',
    parent: 'compass-rotations',
    tab: { label: 'Contracts', order: 1 },
    feature: 'compass_rotations',
    permission: 'compass:rotations:read',
    Component: lazy(() => import('@/custom/compass/rotations/ContractsPage')),
  },
  {
    // SA-1097 (analytics spec §3, R1): the experimental Analytics surface, its own
    // primary entry tagged in the sidebar; Capacity is its first tab and later items
    // add /analytics/scenarios, /analytics/cash and /analytics/ask as children.
    id: 'compass-analytics',
    route: '/analytics',
    nav: { section: 'primary', label: 'Analytics', icon: TrendingUp, tag: 'Experimental' },
    tab: { label: 'Capacity' },
    feature: 'compass_analytics',
    permission: 'compass:analytics:read',
    Component: lazy(() => import('@/custom/compass/analytics/CapacityPage')),
  },
  {
    // SA-1098: the Scenario workbench, the Analytics parent's second tab, before Settings (order 100).
    id: 'compass-analytics-scenarios',
    route: '/analytics/scenarios',
    parent: 'compass-analytics',
    tab: { label: 'Scenarios', order: 10 },
    feature: 'compass_analytics',
    permission: 'compass:analytics:read',
    Component: lazy(() => import('@/custom/compass/analytics/workbench/ScenariosPage')),
  },
  {
    // SA-1099: the cash calendar, the Analytics parent's third tab, under its own permission (the money grant, R3).
    id: 'compass-analytics-cash',
    route: '/analytics/cash',
    parent: 'compass-analytics',
    tab: { label: 'Cash', order: 20 },
    feature: 'compass_analytics',
    permission: 'compass:analytics:cash',
    Component: lazy(() => import('@/custom/compass/analytics/cash/CashPage')),
  },
  {
    // SA-1112: the Analytics settings tab, one section per analysis. Order 100 keeps it last as analysis tabs arrive.
    id: 'compass-analytics-settings',
    route: '/analytics/settings',
    parent: 'compass-analytics',
    tab: { label: 'Settings', order: 100 },
    feature: 'compass_analytics',
    permission: 'compass:analytics:read',
    Component: lazy(() => import('@/custom/compass/analytics/AnalyticsSettingsPage')),
  },
  {
    id: 'compass-pricing-settings',
    route: '/admin/compass/pricing',
    nav: { section: 'admin', label: 'Pricing settings', icon: SlidersHorizontal },
    feature: 'compass_pricing',
    permission: 'compass:manage',
    Component: lazy(() => import('@/custom/compass/PricingSettingsPage')),
  },
]

// ── Slots (E5 spec §7.1) ─────────────────────────────────────────────
// Five places a module adds to a CORE page without the core importing it.
// Every entry is gated like a surface (a flag in no profile + a permission)
// and lazy, so its code loads only where it renders. Each module adds its
// own entries in its own item.

/** A card on a quote-class record's page, after the built-in cards. */
export interface RecordCardProps { order: Order; onChanged: () => void }
export interface RecordCardEntry {
  id: string
  feature: string
  permission: string
  /** Ascending; declaration order breaks ties. Default 0. */
  order?: number
  /** Which record-lane column the card joins (SA-451): `work` (the default)
   * after the built-in work cards; `enquiry` at the top of the enquiry
   * column, before the references. */
  column?: 'work' | 'enquiry'
  Component: LazyExoticComponent<ComponentType<RecordCardProps>>
}

/** A view that replaces the generated task form. Same props, so completion
 * still goes through the page's `onComplete(exit, fields)`. */
export type TaskViewProps = TaskFormProps
export interface TaskViewEntry {
  id: string
  feature: string
  permission: string
  /** Step ids this view is for. No two entries may name the same step. */
  steps: string[]
  /** The module's own contract check (D65), after the step id matched:
   * false keeps the generated form. */
  matches?: (task: TaskRead) => boolean
  Component: LazyExoticComponent<ComponentType<TaskViewProps>>
}

/** A register view (`/orders?view=<id>`) over the same page of rows the
 * built-in register renders. SA-1184: it is a toggle in the register toolbar,
 * beside Priority, never a tab in the Views control. */
export interface RegisterViewEntry {
  id: string
  feature: string
  permission: string
  /** The toggle's words ("Show pricing"). */
  label: string
  /** SA-1183: the view's own server sort keys (`<module>.<name>`), each with its first click,
   *  registered on the server through the module sort hook. Its `sortKeys` may name them. */
  sorts?: ModuleSorts
  /** The column sorts the view's table offers (`COLUMN_SORTS` keys and its own `sorts`); absent
   *  means it sorts by no column. The toggle keeps a column sort only when the
   *  table it switches to offers it (SA-1184, ruling R2). */
  sortKeys?: readonly string[]
  Component: LazyExoticComponent<ComponentType<RegisterViewProps>>
}

/** Columns rendered INTO the built-in register, in place of a core column
 * (SA-453). The entry's component takes the register's own props because it
 * renders the register: it fetches once and passes `columns`. */
export interface RegisterColumnsEntry {
  id: string
  feature: string
  permission: string
  Component: LazyExoticComponent<ComponentType<RegisterViewProps>>
}

export const RECORD_CARDS: RecordCardEntry[] = [
  {
    // SA-450: the record's Contact section, in the enquiry column, before the
    // route card — the wireframe's order. Props-only: it reads the intake
    // snapshot already on the order and fetches nothing.
    id: 'compass-contact-card',
    feature: 'compass',
    permission: 'tasks:read',
    column: 'enquiry',
    Component: lazy(() => import('@/custom/compass/contact/ContactCard')),
  },
  {
    // SA-451: the record's Route section, in the enquiry column. The module
    // flag and the process read's own permission — the card reads nothing else.
    id: 'compass-route-card',
    feature: 'compass',
    permission: 'tasks:read',
    column: 'enquiry',
    Component: lazy(() => import('@/custom/compass/sectors/RouteCard')),
  },
  {
    id: 'compass-pricing-card',
    feature: 'compass_pricing',
    permission: 'orders:read:pricing',
    order: 10,
    Component: lazy(() => import('@/custom/compass/PricingCard')),
  },
  {
    // SA-1459 (spec §7): the record's Agreement section; the accepted quote's contract, its files and rotations. Lazy.
    id: 'compass-agreement-card',
    feature: 'compass_rotations',
    permission: 'compass:rotations:read',
    order: 15,
    Component: lazy(() => import('@/custom/compass/rotations/AgreementSection')),
  },
  {
    // SA-1137 (spec §10): the record's Capacity section; the gaps that could fly this enquiry. Lazy: read on first open.
    id: 'compass-gaps-card',
    feature: 'compass_analytics',
    permission: 'compass:analytics:read',
    order: 20,
    Component: lazy(() => import('@/custom/compass/analytics/record/GapsCard')),
  },
]
export const TASK_VIEWS: TaskViewEntry[] = [
  {
    id: 'compass-propose-rate',
    feature: 'compass_pricing',
    permission: 'orders:read:pricing',
    steps: [STEP_PROPOSE],
    matches: matchesPropose,
    Component: lazy(() => import('@/custom/compass/ProposeRateView')),
  },
  {
    id: 'compass-price-decision',
    feature: 'compass_pricing',
    permission: 'orders:read:pricing',
    steps: [STEP_DECIDE],
    matches: matchesDecide,
    Component: lazy(() => import('@/custom/compass/PriceDecisionView')),
  },
  {
    // SA-1197 (spec C11): step 9's cost minimum pre-filled from the cost
    // sheet's totals. The pricing flag and permission, as steps 10 and 11.
    id: 'compass-rate-references',
    feature: 'compass_pricing',
    permission: 'orders:read:pricing',
    steps: [STEP_RATE_REFERENCES],
    matches: matchesRateReferences,
    Component: lazy(() => import('@/custom/compass/RateReferencesView')),
  },
  {
    // SA-449: step 4's sectors. The module flag, not the pricing one — the
    // rotation is not pricing and its form holds nothing restricted.
    id: 'compass-rotation',
    feature: 'compass',
    permission: 'tasks:read',
    steps: [STEP_ROTATION],
    matches: matchesRotation,
    Component: lazy(() => import('@/custom/compass/sectors/RotationView')),
  },
  {
    // SA-859 (route studies spec D9): Dispatch picks the study a quote is
    // costed on. The studies flag and the read that lists them; anything else
    // keeps the generated form.
    id: 'compass-study-picker',
    feature: 'compass_studies',
    permission: 'compass:studies:read',
    steps: [STEP_DISPATCH],
    matches: matchesStudyPicker,
    Component: lazy(() => import('@/custom/compass/studies/StudyPickerView')),
  },
  {
    // SA-861 (route studies spec D9): at build_cost, the pinned study version's
    // sheet and its figures offered as the pre-fill. The studies flag and the
    // read; anything else keeps the generated form.
    id: 'compass-build-cost',
    feature: 'compass_studies',
    permission: 'compass:studies:read',
    steps: [STEP_BUILD_COST],
    matches: matchesBuildCost,
    Component: lazy(() => import('@/custom/compass/studies/BuildCostView')),
  },
  {
    // SA-1198 (spec §5.5): at route_study, Dispatch authors the study over the base chosen at step 5
    // (or the sectors alone), saves it as a version and pins it. A record pinned to the old step 6
    // (the five typed figures) keeps the generated form: its form carries no `study_reference`.
    id: 'compass-route-study',
    feature: 'compass_studies',
    permission: 'compass:studies:read',
    steps: [STEP_ROUTE_STUDY],
    matches: matchesRouteStudy,
    Component: lazy(() => import('@/custom/compass/studies/RouteStudyView')),
  },
]
export const REGISTER_VIEWS: RegisterViewEntry[] = [
  {
    id: 'pricing',
    feature: 'compass_pricing',
    permission: 'orders:read:pricing',
    label: 'Show pricing',
    // SA-1184: the columns PricingRegisterView sorts by, pinned by its test; SA-1183 adds the
    // role and route columns and the module's own keys, which `sorts` declares.
    sorts: PRICING_SORTS,
    sortKeys: ['reference', 'company', 'contact_name', 'contractual_partner', 'cargo_agent', 'route', 'needed_window',
      'step', 'updated', 'assigned_to', 'status', ...Object.keys(PRICING_SORTS)],
    Component: lazy(() => import('@/custom/compass/PricingRegisterView')),
  },
]
export const REGISTER_COLUMNS: RegisterColumnsEntry[] = [
  {
    // SA-453: the built-in register reads the sectors. The MODULE flag and
    // the register's own permission — a viewer with no pricing sees these.
    id: 'compass-route-columns',
    feature: 'compass',
    permission: 'inbox:read',
    Component: lazy(() => import('@/custom/compass/sectors/RouteColumns')),
  },
]

/** Widgets on the Home page (Home spec 2026-09-23 D2, §3.3). The shape is the
 * core's `HomeWidgetEntry`; the fleet module's entries are here since SA-471
 * and the compass module's since SA-472. */
export const HOME_WIDGETS: HomeWidgetEntry[] = [
  {
    // SA-471 (Home spec D8): the Locator's positions as a strip; the read
    // passes presence=0, so Home never wakes the poller. A candidate since
    // SA-1178 (design pass spec R7): the operation strip covers it, so the
    // tail cards wait in Add.
    id: 'fleet-strip',
    group: 'aircraft',
    title: 'Fleet',
    feature: 'fleet',
    permission: 'fleet:read',
    width: { default: 2 },
    candidate: true,
    blurb: `Where each aircraft is, from the locator’s last positions.`,
    minWidth: 20,
    Component: lazy(() => import('@/custom/fleet/home/FleetStripWidget')),
    Headline: lazy(() => import('@/custom/fleet/home/FleetStripWidget').then((m) => ({ default: m.FleetStripHeadline }))),
  },
  {
    // SA-471 (Home spec D9): the accepted schedule's month — narrow, or wide.
    id: 'block-hours',
    group: 'aircraft',
    title: 'Block hours this month',
    feature: 'fleet_scheduling',
    permission: 'fleet:read',
    width: { default: 1, allowed: [1, 2] },
    blurb: 'Scheduled block hours, flown to date and remaining, per aircraft.',
    minWidth: 16.25,
    Component: lazy(() => import('@/custom/fleet/home/BlockHoursWidget')),
    Headline: lazy(() => import('@/custom/fleet/home/BlockHoursWidget').then((m) => ({ default: m.BlockHoursHeadline }))),
  },
  {
    // SA-1173 (design pass spec §4, ruled R2 and R4): the hero under the header, one lane per tail; the gaps overlay
    // is gated inside (strict on both stores), because an entry declares one permission. First in the default
    // layout since SA-1178 (spec §3.1).
    id: 'operation-strip',
    group: 'aircraft',
    title: 'Operation',
    feature: 'fleet_scheduling',
    permission: 'fleet:read',
    width: { default: 3, allowed: [2, 3] },
    blurb: 'Every aircraft over the next days: sectors, live position, ground gaps.',
    minWidth: 20,
    Component: lazy(() => import('@/custom/fleet/home/OperationStripWidget')),
    Headline: lazy(() => import('@/custom/fleet/home/OperationStripWidget').then((m) => ({ default: m.OperationStripHeadline }))),
  },
  {
    // SA-471 (wireframe decision 50): offered in Add, never on the initial page.
    id: 'todays-flights',
    group: 'aircraft',
    title: `Today’s flights`,
    feature: 'fleet_scheduling',
    permission: 'fleet:read',
    width: { default: 2 },
    candidate: true,
    blurb: `The day’s sectors from the accepted schedule.`,
    minWidth: 22.5,
    Component: lazy(() => import('@/custom/fleet/home/TodaysFlightsWidget')),
    Headline: lazy(() => import('@/custom/fleet/home/TodaysFlightsWidget').then((m) => ({ default: m.TodaysFlightsHeadline }))),
  },
  {
    // SA-472 (Home spec §5.8; wireframe decision 50) offered it in Add only; since SA-1178 it is the eighth row of
    // the default page (design pass spec §3.1), and only with the pricing permission — the rows carry prices, so a
    // viewer without it never sees the card (the page skips it at draw and Add never offers it).
    id: 'decisions-pending',
    group: 'work',
    title: 'Decisions pending',
    feature: 'compass_pricing',
    permission: 'orders:read:pricing',
    width: { default: 2 },
    blurb: 'Awaiting a price decision or a proposal, with the figures on the table.',
    minWidth: 30,
    Component: lazy(() => import('@/custom/compass/home/DecisionsPendingWidget')),
    Headline: lazy(() => import('@/custom/compass/home/DecisionsPendingWidget').then((m) => ({ default: m.DecisionsPendingHeadline }))),
  },
  {
    // SA-1137 (spec §10): the arrival surface for a fitting enquiry; the register's read is gated inside, because an
    // entry declares one permission. The tenth row of the default page since SA-1178 (design pass spec §3.1).
    id: 'demand-matches',
    group: 'customers',
    title: 'Enquiries that fit a gap in the next six weeks',
    feature: 'compass_analytics',
    permission: 'compass:analytics:read',
    width: { default: 2 },
    blurb: 'Open enquiries that a ground gap in the next six weeks could fly, with the best gap for each.',
    minWidth: 22.5,
    Component: lazy(() => import('@/custom/compass/analytics/home/DemandMatchesWidget')),
    Headline: lazy(() => import('@/custom/compass/analytics/home/DemandMatchesWidget').then((m) => ({ default: m.DemandMatchesHeadline }))),
  },
  {
    // SA-1172 (design pass spec §6, ruled R3): one paragraph over the reads the
    // lists make, full width by default; each clause gates its own read inside,
    // because an entry declares one permission. Third in the default layout
    // since SA-1178 (spec §3.1).
    id: 'briefing',
    group: 'business',
    title: 'Briefing',
    feature: 'compass',
    permission: 'inbox:read',
    width: { default: 3, allowed: [2, 3] },
    blurb: 'What the operation and the desk look like right now, in one paragraph.',
    minWidth: 15,
    Component: lazy(() => import('@/custom/compass/home/BriefingWidget')),
    Headline: lazy(() => import('@/custom/compass/home/BriefingWidget').then((m) => ({ default: m.BriefingHeadline }))),
  },
]

/** Tiles on the Home page's At a glance widget (SA-1171, spec §2.2): the
 * core's `HomeTileEntry`; the fleet module's two tiles arrive in the same item. */
export const HOME_TILES: HomeTileEntry[] = [
  {
    // SA-1171 (spec §4): the airborne tails, over the strip's read.
    id: 'airborne-now', title: 'Airborne now', feature: 'fleet', permission: 'fleet:read', order: 10,
    Component: lazy(() => import('@/custom/fleet/home/tiles/AirborneNowTile')),
  },
  {
    // SA-1171 (spec §4): flown over scheduled this month, the Block hours widget's read.
    id: 'block-hours-mtd', title: 'Block hours month to date', feature: 'fleet_scheduling', permission: 'fleet:read', order: 20,
    Component: lazy(() => import('@/custom/fleet/home/tiles/BlockHoursMtdTile')),
  },
]

/** A component mounted ONCE in the app shell, beside the core's own notice hook (SA-1143): it shows nothing on a
 * page and listens (the inbox event bus) for its module's events, toasting through the app's one toaster. Gated
 * like every slot entry, so its code loads only for a viewer who may use it. */
export interface AppListenerEntry {
  id: string
  feature: string
  permission: string
  Component: LazyExoticComponent<ComponentType>
}

export const APP_LISTENERS: AppListenerEntry[] = [
  {
    // SA-1143 (spec §6.3): a toast when an arriving enquiry fits a ground gap; the register's read is checked inside,
    // because an entry declares one permission (ruling S30's shape).
    id: 'capacity-fit-notices',
    feature: 'compass_analytics',
    permission: 'compass:analytics:read',
    Component: lazy(() => import('@/custom/compass/analytics/notices/FitNotices')),
  },
]

export type { HomeTileEntry, HomeTileProps, HomeWidgetEntry, HomeWidgetProps, HomeWidth } from '@/components/custom/homeWidgetTypes'

/** Children of a top-level entry, in tab order (declaration order breaks ties). */
export function childSurfaces(parentId: string): SurfaceEntry[] {
  return SURFACES.filter((s) => s.parent === parentId).sort((a, b) => (a.tab?.order ?? 0) - (b.tab?.order ?? 0))
}

export { SurfaceGuard } from './SurfaceGuard'
export { FleetShell } from './fleet/FleetShell'
